import { useCallback } from 'react';
import styled from '@emotion/styled';
import { XToolConnector } from '@hotjar/insights-common/index';
import { useFeedbackResponseQuery, useFeedbackWidgetQuery } from '@hotjar/feedback';
import { useTranslation } from '@hotjar/translations';
import { useSite } from '@hotjar/site';
import { EmotionIcon } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import { EMOTION_COLORS } from '@hotjar/insights-common/constants/emotions';

type Scores = 0 | 1 | 2 | 3 | 4;

const VALID_SCORES: Partial<Record<number, Scores>> = { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4 };

const scoreLabelKeys: Record<Scores, string> = {
  0: 'hate',
  1: 'dislike',
  2: 'neutral',
  3: 'like',
  4: 'love',
};

// Create version of emotion colors with 89% transparency
const lineColors: Record<Scores, string> = {
  0: `${EMOTION_COLORS[0]}e3`,
  1: `${EMOTION_COLORS[1]}e3`,
  2: `${EMOTION_COLORS[2]}e3`,
  3: `${EMOTION_COLORS[3]}e3`,
  4: `${EMOTION_COLORS[4]}e3`,
};

const StarEmotionIcon = styled(EmotionIcon)`
  position: relative;
  bottom: 2px;
`;

export type FeedbackConnectorProps = {
  emotion?: number;
  targetUrl: string;
  siteId: number;
  responseId: string | number;
  widgetId: string | number;
};

export const FeedbackConnector = ({
  siteId,
  widgetId,
  responseId,
  emotion,
  targetUrl,
}: FeedbackConnectorProps): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-connector' });
  const { data: site } = useSite(siteId);
  const { data: response, isLoading } = useFeedbackResponseQuery(
    site,
    Number(widgetId),
    Number(responseId),
  );
  const { data: widget } = useFeedbackWidgetQuery(site, Number(widgetId));

  const openFeedback = useCallback(() => {
    window.location.assign(targetUrl);
  }, [targetUrl]);

  // this just ensures our emotion number is a score from 0 - 4
  const score = emotion === undefined ? 3 : VALID_SCORES[emotion] ?? 3;

  return (
    <XToolConnector
      title={
        <>
          {widget?.emotion_style === 'star' && (
            <StarEmotionIcon score={score} emotionStyle="star" size="s" />
          )}
          {widget?.emotion_style !== 'star' && (
            <EmotionIcon score={score} emotionStyle={widget?.emotion_style ?? 'default'} size="m" />
          )}
          {t(scoreLabelKeys[score])}
        </>
      }
      description={t('description')}
      helperText={response?.content.message}
      errorMessage={!isLoading && response === undefined ? t('no-response-error') : undefined}
      isLoading={isLoading}
      lineColor={lineColors[score]}
      actionButtonText={t('view-response')}
      actionButtonIcon="feedback"
      actionButtonDisabled={response === undefined}
      onActionButtonClick={openFeedback}
    />
  );
};
